import { useState, useEffect, useCallback } from 'react';
import { gql } from '@apollo/client';
import { apolloClient } from 'core/apollo-client';
import { uploadOfferDocument } from 'offer/helpers/offer-document-service';
import { isCustomerSpecificTask } from '@ourbranch/onboarding-tasks';

const GET_ONBOARDING_TASKS_FOR_OFFER = gql`
  query GetOnboardingTasksForOffer($offerId: ID!) {
    getOnboardingTasksForOffer(offerId: $offerId) {
      ... on OfferOnboardingTasksResponse {
        success
        tasks {
          taskName
          allowedForUpload
          data
        }
        documents {
          taskName
          fileName
          uploadedAt
          path
          customer
        }
      }
      ... on OfferOnboardingTasksError {
        success
        error
      }
    }
  }
`;

const DELETE_OFFER_TASK_DOCUMENT = gql`
  mutation DeleteOfferTaskDocument($webUserId: String!, $documentPath: String!) {
    deleteOfferTaskDocument(webUserId: $webUserId, documentPath: $documentPath) {
      success
      error
    }
  }
`;

/**
 * Hook for managing offer onboarding tasks
 * @param {string} offerId - The offer ID
 * @returns {Object} - Tasks state and actions
 */
export const useOfferTasks = (offerId, webUserId, staffUsername) => {
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Fetch tasks for the offer
  const fetchTasks = useCallback(async () => {
    if (!offerId) {
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const { data } = await apolloClient.query({
        query: GET_ONBOARDING_TASKS_FOR_OFFER,
        variables: { offerId },
        fetchPolicy: 'no-cache'
      });

      const result = data?.getOnboardingTasksForOffer;

      if (result) {
        // Check if it's an error response
        if (result.error) {
          setError(result.error);
          setTasks([]);
          return;
        }

        // Handle success response
        const { tasks: calculatedTasks = [] } = result;

        // Transform tasks to match component expectations
        const transformedTasks = calculatedTasks.map((task) => {
          // Parse data if it's a string (AWSJSON might come as string) - DO THIS FIRST
          let parsedData = task.data || {};
          if (typeof parsedData === 'string') {
            try {
              parsedData = JSON.parse(parsedData);
            } catch (e) {
              console.error('Failed to parse task data:', e);
              parsedData = {};
            }
          }

          // Create unique taskId that includes customer for customer-specific tasks
          const taskId =
            isCustomerSpecificTask(task.taskName) && parsedData?.customer
              ? `${offerId}-${task.taskName}-${parsedData.customer}`
              : `${offerId}-${task.taskName}`;

          // Extract document info from data
          const hasDocument = parsedData.hasDocument || false;
          const files = parsedData.files || [];
          const firstFile = files[0];

          return {
            taskId,
            taskName: task.taskName,
            data: parsedData,
            completed: hasDocument,
            completedDate: firstFile?.uploadedDate || null,
            completedBy: firstFile?.uploadedBy || 'Staff',
            documentUrl: firstFile?.documentUrl || null,
            files: files.map((file) => ({
              fileName: file.fileName,
              documentUrl: file.documentUrl
            }))
          };
        });

        setTasks(transformedTasks);
      }
    } catch (err) {
      console.error('Error fetching offer tasks:', err);
      setError(err.message || 'Failed to fetch tasks');
    } finally {
      setLoading(false);
    }
  }, [offerId]);

  // Upload document for a task
  const uploadDocument = useCallback(
    async ({ file, taskName, taskId, onProgress }) => {
      if (!offerId) {
        throw new Error('No offer ID provided');
      }

      if (!webUserId) {
        throw new Error('No webUserId available');
      }

      try {
        // Find the task by taskId to get customer information for customer-specific tasks
        const task = tasks.find((t) => t.taskId === taskId);
        const customer = task?.data?.customer;

        await uploadOfferDocument(webUserId, file, {
          taskName,
          offerId,
          onProgress,
          uploadedBy: staffUsername,
          customer // Pass customer for customer-specific tasks
        });

        // Wait a moment for S3 consistency before refreshing
        await new Promise((resolve) => setTimeout(resolve, 1000));

        // Refresh tasks after upload
        await fetchTasks();
      } catch (error) {
        console.error('Error uploading document:', error);
        throw error;
      }
    },
    [offerId, webUserId, fetchTasks, staffUsername, tasks]
  );

  // Delete document for a task
  const deleteDocument = useCallback(
    async (file) => {
      if (!webUserId) {
        throw new Error('No webUserId available');
      }

      let documentPath = file.documentUrl;
      if (!documentPath) {
        throw new Error('No document path provided');
      }

      // Strip s3:// prefix if present (e.g., "s3://bucket/key" -> "key")
      if (documentPath.startsWith('s3://')) {
        const s3UriMatch = documentPath.match(/^s3:\/\/[^/]+\/(.+)$/);
        if (s3UriMatch) {
          documentPath = s3UriMatch[1];
        }
      }

      try {
        const { data } = await apolloClient.mutate({
          mutation: DELETE_OFFER_TASK_DOCUMENT,
          variables: {
            webUserId,
            documentPath
          }
        });

        if (data?.deleteOfferTaskDocument?.error) {
          throw new Error(data.deleteOfferTaskDocument.error);
        }

        // Wait a moment for S3 consistency before refreshing
        await new Promise((resolve) => setTimeout(resolve, 1000));

        // Refresh tasks after delete
        await fetchTasks();
      } catch (error) {
        console.error('Error deleting document:', error);
        throw error;
      }
    },
    [webUserId, fetchTasks]
  );

  // Fetch tasks on mount and when offerId changes
  useEffect(() => {
    fetchTasks();
  }, [fetchTasks]);

  // Separate tasks into incomplete and completed
  const incompleteTasks = tasks.filter((task) => !task.completed);
  const completedTasks = tasks.filter((task) => task.completed);

  return {
    tasks,
    incompleteTasks,
    completedTasks,
    loading,
    error,
    fetchTasks,
    uploadDocument,
    deleteDocument
  };
};
